import { Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslatePipe } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { Subject, throwError } from "rxjs";
import { catchError, debounceTime, tap } from "rxjs/operators";
import { MasterDataDataService } from "src/app/share/data-service/master-data.data.service";
import { OrderDataService } from "src/app/comparison/data-service/order-data.service";
import {
	ProductInterface,
	QuotationInterface,
	VehicleListingInterface,
} from "src/app/comparison/interface/motor-quotation.interface";
import { AlertService } from "src/app/share/service/alert.service";
import { UserDataService } from 'src/app/share/data-service/user.data.service';

@Component({
	selector: 'app-edit-quotations-new',
	templateUrl: './edit-quotations-new.component.html',
	styleUrls: ['./edit-quotations-new.component.scss'],
	providers: [TranslatePipe],
})
export class EditQuotationsNewComponent {
	vehicleList: VehicleListingInterface[] = [];
	limit: number = 20;
	offset: number = 1;
	isCompleted: boolean = false;

	searchChange: Subject<void> = new Subject<void>();
	searchText: string;

	selectVehicle: boolean = false;

	isLoading: boolean = false;

	originalMotorProductList: ProductInterface[] = [];
	motorProductList: ProductInterface[] = [];
	filterMotorProductList: ProductInterface[] = [];
	comMotorProductList: ProductInterface[];
	tpftMotorProductList: ProductInterface[];
	allMotorProductList: ProductInterface[];

	formGroup: UntypedFormGroup;
	initialFormValues : any;
	initialSumValues : any;
	initialSumRangeValues : any;


	firstInit: boolean = true;
	istouch: boolean = false;

	hideTP: boolean = false;

	currentData: Record<string, any> = {};

  private sumInsuredList = this.data.quotationList
    .filter((x) => x.status !== "New" && x.status !== "Rejected")
    .map((x) => x.sumInsured);
		
	constructor(
		private masterDataDataService: MasterDataDataService,
		private orderDataService: OrderDataService,
		private ngxSpinnerService: NgxSpinnerService,
		private alertService: AlertService,
		public dialogRef: MatDialogRef<EditQuotationsNewComponent>,
		public userDataService: UserDataService,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			id: string;
			request: any;
			quotationList: QuotationInterface[];
		},
		private translatePipe: TranslatePipe,
	) {
		this.searchText = data.request.vehicle;

		this.initForm();
		this.initialFormValues = [...this.formGroup.controls.selectedMotorProductList.value];
        this.initialSumValues = this.formGroup.controls.sumInsured.value;
				this.initialSumRangeValues = this.formGroup.controls.sumInsuredRange.value;

        this.formGroup.valueChanges.subscribe(() => {
            this.onFieldChange();
        });

		this.getVehicleListing();
		this.getAllProduct();

		this.searchChange.pipe(debounceTime(500)).subscribe((x) => {
			this.offset = 1;
			this.vehicleList = [];
			this.getVehicleListing();
		});

		this.formGroup.controls.insuranceType.valueChanges.subscribe((value) => {
			this.getAllProduct(value);
			this.formGroup.controls.selectedMotorProductList.setValue([]);
			this.filterMotorProductSelection();
		});
	}

	private initForm() {
		this.formGroup = new UntypedFormGroup({
			vehicleId: new UntypedFormControl(null),
			typeOfSum: new UntypedFormControl(this.data.request.typeOfSum ?? "MV", [
				Validators.required,
			]),
			insuranceType: new UntypedFormControl(this.data.request.insuranceType, [
				Validators.required,
			]),
			sumInsured: new UntypedFormControl(Math.min(...this.sumInsuredList)),
			sumInsuredRange: new UntypedFormControl(this.data.quotationList[0].sumInsuredRange),
			selectedMotorProductList: new UntypedFormControl([]),
		}, { validators: sumInsuredOrProductSelectedValidator });

		// ## Specific requirement: For grab TFPT include generali
		this.formGroup.controls.insuranceType.valueChanges.subscribe((x) => {
			if (x == "COM") {
				this.motorProductList = this.originalMotorProductList.filter(
					(x: any) => x.insuranceType == "COM" || x.insuranceType == null,
				);
			}
			if (x == "TPFT") {
				this.motorProductList = this.originalMotorProductList.filter(
					(x: any) => x.insuranceType == "TPFT" || x.insuranceType == null,
				);
			}
		});

				this.userDataService
					.getProfile()
					.pipe(
						catchError((err) => {
							return throwError(err);
						}),
						tap((userData) => {
							if (userData.type === "FUNDINGSOCIETIES") {
								this.hideTP = true;
							}
		
						})
					)
					.subscribe();
			
	}

	private getVehicleListing() {
		this.isLoading = true;
		this.masterDataDataService
			.getVehicleModelListing(
				this.limit,
				this.offset,
				this.searchText, 1, "",
				this.data.request.make,
			)
			.subscribe({
				next: (x) => {
					this.isCompleted = x.length < this.limit;
					this.vehicleList = this.vehicleList.concat(x);
					this.isLoading = false;
					let vehicleId: string;
					if (!this.firstInit) {
						return;
					}
					this.firstInit = false;

					if (this.vehicleList.length != 0) {
						vehicleId = this.vehicleList[0].id;
						this.formGroup.controls.vehicleId.setValue(vehicleId);
					}

					this.currentData = {
						vehicleId,
						typeOfSum: this.data.request.typeOfSum ?? "MV",
						insuranceType: this.data.request.insuranceType,
						sumInsured: Math.min(...this.sumInsuredList),
						sumInsuredRange: this.data.quotationList.map(
							(x) => x.sumInsuredRange,
						),
						selectedMotorProductList: this.data.quotationList.map(
							(x) => x.productId,
						),
					};
				},
			});
	}

	private getAllProduct(insuranceType: string = null) {
		this.isLoading = true;
		this.orderDataService.getAllProductByDealerType(this.data.id, this.data.request.partnerCode, this.data.request.dealerType).subscribe({
			next: (x: any) => {
				this.isLoading = false;
				this.originalMotorProductList = x;
				// Default insuranceType is COM, so show COM dropdown list
				this.motorProductList = this.originalMotorProductList.filter(
					(x: any) => x.insuranceType == "COM" || x.insuranceType == null,
				);
				this.filterMotorProductSelection();
			},
			error: (err) => {
				this.isLoading = false;
			},
		});
	}

	sumInsuredChange(range: any) {
		if (range.value == "Lowest") {
			this.formGroup.controls.sumInsured.setValue(this.getSumInsuredConfig().min);
		}
		else {
			this.formGroup.controls.sumInsured.setValue(this.getSumInsuredConfig().max);

		}
	}

	clearVehicle() {
		event.stopImmediatePropagation();
		this.formGroup.controls.vehicleId.setValue(null);
	}

	getSelectedProductList() {
		return this.formGroup.value.selectedMotorProductList;
	}

	filterMotorProductSelection(searchValue: string = null) {
		if (searchValue) {
			let txt = searchValue.toLowerCase().trim();
			this.filterMotorProductList = this.motorProductList.filter((x) =>
				x.name.toLowerCase().includes(txt),
			);
		} else {
			this.filterMotorProductList = Object.assign([], this.motorProductList);
		}
	}

	isSelectedAllMotorProduct() {
		return this.getSelectedProductList().length == this.motorProductList.length;
	}

	isIndeterminateMotorProduct() {
		if (this.isSelectedAllMotorProduct()) return false;
		return this.getSelectedProductList().length > 0;
	}

	toggleSelectAllMotorProduct(val: boolean) {
		this.formGroup.controls.selectedMotorProductList.setValue([]);
		if (val) {
			this.formGroup.controls.selectedMotorProductList.setValue(
				this.motorProductList.map((y) => y.id),
			);
			this.formGroup.controls.selectedMotorProductList.markAsDirty();
		}
	}

	getMotorProductDisplay() {
		if (this.motorProductList.length == 0) return null;
		if (this.getSelectedProductList().length == 1)
			return this.motorProductList.find(
				(x) => x.id == this.getSelectedProductList()[0],
			).name;
		else if (this.isSelectedAllMotorProduct())
			return this.translatePipe.transform("common.allInsurer");
		else
			return `${this.getSelectedProductList().length
				} ${this.translatePipe.transform("common.insurerSelected")}`;
	}

	resetMotorProduct() {
		event.stopImmediatePropagation();
		this.toggleSelectAllMotorProduct(true);
	}

	filterVehicle() {
		if (!this.selectVehicle) this.searchChange.next();
		else this.selectVehicle = false;
	}

	getNextBatch() {
		if (!this.isCompleted) {
			this.offset++;
			this.getVehicleListing();
		}
	}

	onFieldChange() {
		const sumInsuredCurrentValue = Number(this.formGroup.controls.sumInsured.value);
		const sumInsuredInitialValue = Number(this.initialSumValues);
		const selectedMotorProductCurrentValue = JSON.stringify(this.formGroup.controls.selectedMotorProductList.value);
		const selectedMotorProductInitialValue = JSON.stringify(this.initialFormValues);
		const insuranceTypeCurrentValue = this.formGroup.controls.insuranceType.value;
		const insuranceTypeInitialValue = this.data.request.insuranceType;
		const sumInsuredRangeCurrentValue = this.formGroup.controls.sumInsuredRange.value;
		const sumInsuredRangeInitialValue = this.initialSumRangeValues;
	
		this.istouch = 
			sumInsuredCurrentValue !== sumInsuredInitialValue ||
			selectedMotorProductCurrentValue !== selectedMotorProductInitialValue ||
			insuranceTypeCurrentValue !== insuranceTypeInitialValue || 
			sumInsuredRangeCurrentValue !== sumInsuredRangeInitialValue;
	
		this.isDisabledSubmit();
	}

	isDisabledSubmit(): boolean {
		if (this.formGroup.invalid || !this.istouch) {
			return true;
		}

		return this.isSelectionAndCurrentEqual();
	}

	save() {
		if (this.formGroup.invalid) return;

		this.ngxSpinnerService.show();
		let value = Object.assign({}, this.formGroup.value);
		if (!value.selectedMotorProductList?.length) {
			value.selectedMotorProductList = this.data.quotationList.map(
				(x) => x.productId,
			);
		}

		this.orderDataService.editRequest(this.data.id, value).subscribe({
			next: () => {
				this.ngxSpinnerService.hide();
				this.dialogRef.close(true);
			},
			error: (err) => {
				this.ngxSpinnerService.hide();
				this.alertService.openSnackBar(err.error);
			},
		});
	}

	getSumInsuredConfig(): ISumInsuredConfig {
		let min = Math.min(...this.sumInsuredList);
		let max = +(min * 1.1).toFixed(2);
		let sumInsured = min;

		// Adjust sum insured if Chubb is selected
		const ChubbId = this.motorProductList.find((x) => x.code === 'Chubb')?.id ?? '';
		const isChubbSelected = this.getSelectedProductList().includes(ChubbId);
		if (isChubbSelected) {
			const isChubbFound = !!this.data.quotationList.find((x) => x.code === 'Chubb');
			if (isChubbFound) sumInsured = this.data.quotationList.find((x) => x.code === 'Chubb')?.sumInsured;
			max = this.data.request.businessType === 'TransferOwnership' ? +(sumInsured * 1.7).toFixed(2) : +(sumInsured * 1.2).toFixed(2);
			min = +(sumInsured * 0.9).toFixed(2);
		}

		return {
			min,
			max,
			minError: "common.sumInsuredMinError",
			maxError: "common.sumInsuredMaxError",
			sumInsured
		}
	}

	private isSelectionAndCurrentEqual(): boolean {
		this.currentData = {
			typeOfSum: this.data.request.typeOfSum ?? "MV",
			insuranceType: this.data.request.insuranceType,
			sumInsured: Math.min(...this.sumInsuredList),
			sumInsuredRange: this.initialSumRangeValues
		};

		const isVehicleIdEmpty = !this.formGroup.value.vehicleId;
		const isInsuranceTypeEqual =
			this.formGroup.value.insuranceType === this.currentData.insuranceType;
		const isSumInsuredEqual =
			+this.formGroup.value.sumInsured === this.currentData.sumInsured;
		const isOtherInsurerEmpty =
			this.formGroup.value.selectedMotorProductList.length == 0;
			const isSumInsuredRangeEqual = 
		this.formGroup.value.sumInsuredRange === this.currentData.sumInsuredRange;

		return (
			isVehicleIdEmpty &&
			isInsuranceTypeEqual &&
			isSumInsuredEqual &&
			isOtherInsurerEmpty && 
			isSumInsuredRangeEqual
		);
	}
}

function sumInsuredOrProductSelectedValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
	const { sumInsured, selectedMotorProductList } = formGroup.value;
	const isSumInsuredEmpty = !sumInsured;
	const isSelectedMotorProductListEmpty = !selectedMotorProductList || selectedMotorProductList.length === 0;

	if (isSumInsuredEmpty && isSelectedMotorProductListEmpty) {
		return { requiredSumInsuredOrProduct: true };
	}
	return null;
}

interface ISumInsuredConfig {
	min: number;
	max: number;
	minError: string;
	maxError: string;
	sumInsured: number;
}